// Admin provisioning list for CP Sourcing team members: create their SmartFlo
// (Tata) account exactly like Sales Managers. No contact group / lead import here.
import { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, CircularProgress,
} from "@mui/material";
import CreateUserModal from "./CreateUserModal";
import CreateGroupModal from "./CreateGroupModal";
import SearchBar from "../../Subcomponents/Search";
import { getAllCpSourcingUsers } from "../../Services/SmartFloApis";
import { createCpGroupAndUpload } from "../../Services/CpSourcingApis";

export default function CpSourcingTeamList() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [groupMember, setGroupMember] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await getAllCpSourcingUsers();
    if (res.success) setMembers(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = members.filter(
    (m) =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUserCreated = (memberId, smartfloUserId) => {
    setMembers((prev) =>
      prev.map((m) =>
        m.userId === memberId
          ? { ...m, isTeleAccountCreated: true, smartfloUserId: smartfloUserId || m.smartfloUserId }
          : m
      )
    );
  };

  const handleGroupCreated = (memberId, smartfloGroupId) => {
    setMembers((prev) =>
      prev.map((m) =>
        m.userId === memberId
          ? { ...m, isContactGroupCreated: true, contactGroupId: smartfloGroupId || m.contactGroupId }
          : m
      )
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, fontFamily: "Inter-Medium" }}>CP Sourcing Team</Typography>
        {/* Search — shared SearchBar so the fill, radius, icon size and focus
            ring match Leads and the other SmartFlo tabs. 25rem is the Leads
            standard width (.wrapperSearchBar in Leads.module.css). No ml:auto
            needed: the parent Box already uses justifyContent space-between. */}
        <Box sx={{ width: "25rem", maxWidth: "100%" }}>
          <SearchBar
            id="cpSourcingTeamSearch"
            placeholder="Search name or email…"
            search={(e) => setSearch(e.target.value)}
            width="100%"
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Name", "Email", "Assigned CPs", "Status", "SmartFlo", "Actions"].map((h) => (
                <TableCell
                  key={h}
                  align={h === "Name" || h === "Email" ? "left" : "center"}
                  sx={{ fontSize: "12.5px", fontWeight: 600, color: "#6b7280", py: 1.5 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ fontSize: "12.5px", color: "#6b7280" }}>
                No CP Sourcing users yet. Create them via Users → Create User (type "CP Sourcing").
              </TableCell></TableRow>
            )}
            {filtered.map((m) => (
              <TableRow
                key={m.userId}
                hover
                sx={{ "& td": { fontSize: "12px", py: 1.4, borderBottom: "1px solid #f1f5f9" } }}
              >
                <TableCell sx={{ fontWeight: 500, fontFamily: "Inter-Medium", fontSize: "12px!important" }}>{m.name}</TableCell>
                <TableCell sx={{ color: "#6b7280", fontFamily: "Inter-Medium", fontSize: "12px!important" }}>{m.email}</TableCell>
                <TableCell align="center" sx={{ fontFamily: "Inter-Medium", fontSize: "12px!important" }}>
                  {m.cpCount > 0 ? m.cpCount : <em style={{ color: "#98A2B3" }}>none</em>}
                </TableCell>
                <TableCell align="center">
                  <Chip size="small" label={m.isActive ? "Active" : "Inactive"}
                    color={m.isActive ? "success" : "default"}
                    sx={{ fontSize: "12px", fontFamily: "Inter-Medium" }} />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    <Chip size="small" label={m.isTeleAccountCreated ? "Account ✓" : "No account"}
                      color={m.isTeleAccountCreated ? "success" : "warning"}
                      sx={{ fontSize: "12px", fontFamily: "Inter-Medium" }} />
                    <Chip size="small" label={m.isContactGroupCreated ? "Group ✓" : "No group"}
                      color={m.isContactGroupCreated ? "success" : "default"}
                      sx={{ fontSize: "12px", fontFamily: "Inter-Medium" }} />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {!m.isTeleAccountCreated ? (
                    <Button size="small" variant="contained"
                      sx={{ textTransform: "none", fontSize: "13px !important", fontFamily: "Inter-Medium" }}
                      onClick={() => setSelectedMember(m)}>
                      + Create Account
                    </Button>
                  ) : !m.isContactGroupCreated ? (
                    <Button size="small" variant="outlined"
                      sx={{ textTransform: "none", fontSize: "13px !important", fontFamily: "Inter-Medium" }}
                      onClick={() => setGroupMember(m)}>
                      Create Group
                    </Button>
                  ) : (
                    <Chip size="small" label="Set Up Completed" color="success" variant="outlined"
                      sx={{ fontSize: "12px", fontFamily: "Inter-Medium" }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CreateUserModal
        open={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        manager={selectedMember ? { id: selectedMember.userId, name: selectedMember.name, email: selectedMember.email } : null}
        onSuccess={handleUserCreated}
      />

      <CreateGroupModal
        open={!!groupMember}
        onClose={() => setGroupMember(null)}
        manager={groupMember ? { id: groupMember.userId, name: groupMember.name, smartfloUserId: groupMember.smartfloUserId } : null}
        onSuccess={handleGroupCreated}
        submitApi={createCpGroupAndUpload}
      />
    </Box>
  );
}
